import { useEffect, useRef, useState } from 'react';
import { trackEvent } from '../../../utils/analytics';

const points = [
  {
    icon: 'ri-alert-line',
    title: 'Il cassonetto è il punto più critico di tutta la finestra',
    desc: 'La maggior parte del freddo che entra in casa non viene dalle vetrate, ma dal cassonetto della tapparella. È una cavità vuota che fa da ponte termico diretto con l\'esterno — e molte imprese non te lo dicono.',
  },
  {
    icon: 'ri-funds-line',
    title: 'Spendi migliaia in serramenti nuovi e poi lasci la falla aperta',
    desc: 'Cambi le finestre con vetri triplo strato, investi nell\'isolamento, e poi il cassonetto non coibentato vanifica tutto. È come comprare una porta blindata e lasciare la finestra spalancata.',
  },
  {
    icon: 'ri-building-line',
    title: 'La tua impresa può installarli — noi le forniamo il materiale',
    desc: 'Non devi cercare un\'altra ditta. Noi forniamo i cassonetti coibentati certificati U\u209b\u209c=1\u00a0W/m\u00b2K direttamente alla tua impresa di fiducia, su misura, in 48 ore. Tu chiedi, loro installano.',
  },
];

export default function B2cSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id="privati"
      aria-label="Stai ristrutturando casa? Chiedi alla tua impresa i cassonetti coibentati Punto Cassonetti"
      className="bg-[#0D0D0D] py-20 md:py-28 overflow-hidden relative"
    >
      {/* Subtle background texture */}
      <div className="absolute inset-0 opacity-20">
        <img
          src="https://public.readdy.ai/ai/img_res/a65e220646fa648bbe2b5de804e68a75.jpg"
          alt=""
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D] via-transparent to-[#0D0D0D]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10">

        {/* Alert badge */}
        <div
          className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} flex justify-center mb-8`}
        >
          <span
            className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            <i className="ri-alert-fill text-sm" />
            Per chi sta ristrutturando casa
          </span>
        </div>

        {/* Big provocative headline */}
        <div className={`transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} text-center mb-6 max-w-4xl mx-auto`}>
          <h2
            className="font-black text-white leading-[1.1] tracking-tighter"
            style={{ fontFamily: "'Raleway', sans-serif", fontSize: 'clamp(1.9rem, 5vw, 3.8rem)' }}
          >
            La tua impresa sta ristrutturando casa
            <span className="text-amber-400"> ma non ti ha ancora</span> parlato dei cassonetti coibentati?
          </h2>
        </div>

        {/* Sub headline */}
        <div className={`transition-all duration-700 delay-150 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} text-center mb-16 max-w-2xl mx-auto`}>
          <p
            className="text-white/50 text-base md:text-lg leading-relaxed"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Il cassonetto della tapparella è la prima causa di dispersione termica in casa.
            Eppure spesso nessuno lo nomina durante i lavori.{' '}
            <strong className="text-white/75">Ecco perché.</strong>
          </p>
        </div>

        {/* 3 points */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-16">
          {points.map((p, i) => (
            <div
              key={p.title}
              className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} bg-white/5 border border-white/8 rounded-2xl p-7 hover:border-amber-400/25 hover:bg-amber-400/5 transition-all group`}
              style={{ transitionDelay: `${200 + i * 100}ms` }}
            >
              <div className="w-11 h-11 flex items-center justify-center bg-amber-400/10 rounded-xl mb-5 group-hover:bg-amber-400/20 transition-colors">
                <i className={`${p.icon} text-amber-400 text-xl`} />
              </div>
              <h3
                className="text-white font-bold text-base leading-snug mb-3"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                {p.title}
              </h3>
              <p
                className="text-white/40 text-sm leading-relaxed"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {p.desc}
              </p>
            </div>
          ))}
        </div>

        {/* CTA block — full-width card */}
        <div
          className={`transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} relative rounded-2xl overflow-hidden`}
        >
          {/* Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/15 via-amber-400/8 to-transparent border border-amber-400/20 rounded-2xl" />

          <div className="relative z-10 p-8 md:p-12 flex flex-col lg:flex-row items-start lg:items-center gap-8 justify-between">
            {/* Left: text */}
            <div className="flex-1 min-w-0">
              <div
                className="text-amber-400 text-xs font-bold uppercase tracking-widest mb-3"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                — Come funziona
              </div>
              <h3
                className="text-white font-black text-2xl md:text-3xl leading-snug tracking-tight mb-3"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                Contattaci: ti aiutiamo a fare la richiesta alla tua impresa
              </h3>
              <p
                className="text-white/45 text-sm leading-relaxed max-w-xl"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                Ti diciamo esattamente cosa chiedere, forniamo schede tecniche certificate e quotazioni dirette all'impresa che sta lavorando in casa tua.
                Nessuna complessità per te, massimo risultato per l'isolamento termico della tua abitazione.
              </p>

              {/* Mini steps */}
              <div className="mt-6 flex flex-col sm:flex-row gap-4">
                {[
                  { n: '1', t: 'Ci contatti e ci descrivi i lavori in corso' },
                  { n: '2', t: 'Ti inviamo tutto il materiale da girare alla tua impresa' },
                  { n: '3', t: 'La tua impresa installa i cassonetti coibentati certificati' },
                ].map((step) => (
                  <div key={step.n} className="flex items-start gap-3">
                    <span
                      className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-amber-400/20 text-amber-400 font-black text-xs"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {step.n}
                    </span>
                    <span
                      className="text-white/50 text-xs leading-relaxed"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {step.t}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: CTAs */}
            <div className="flex flex-col gap-3 flex-shrink-0 w-full lg:w-auto">
              <a
                href="https://wa.me/393939932439?text=Ciao%2C%20sto%20ristrutturando%20casa%20e%20vorrei%20sapere%20se%20la%20mia%20impresa%20pu%C3%B2%20installare%20i%20vostri%20cassonetti%20coibentati."
                target="_blank"
                rel="noopener noreferrer nofollow"
                onClick={() => trackEvent('whatsapp_click', 'B2C section – WhatsApp privati', 'b2c_section')}
                className="flex items-center justify-center gap-2.5 bg-green-500 hover:bg-green-600 text-white font-black px-8 py-4 rounded-xl transition-all text-sm whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <i className="ri-whatsapp-line text-lg" />
                Scrivici su WhatsApp
              </a>
              <a
                href="#form"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('form')?.scrollIntoView({ behavior: 'smooth' });
                  trackEvent('preventivo_cta', 'B2C section – form privati', 'b2c_section');
                }}
                className="flex items-center justify-center gap-2.5 border border-amber-400/30 hover:border-amber-400/60 text-amber-400 hover:text-amber-300 font-semibold px-8 py-4 rounded-xl transition-all text-sm whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <i className="ri-mail-send-line" />
                Oppure inviaci un messaggio
              </a>
              <p
                className="text-white/25 text-[11px] text-center"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                Nessun impegno · Risposta entro 24h
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
